import React from 'react';
import BelegKarte from '../BelegKarte';
import Wochenplan, { WochenTag } from './Wochenplan';
import {
  BelegKarte as BelegKarteDaten, BoardEintrag, BoardKachel as BoardKachelDaten, BoardZiel,
} from '../../types';
import { navigiere } from '../../lib/navigation';
import { zielErreichbar } from '../../lib/tabRollen';
import { zeilenSchluessel } from '../../lib/board';

const NICHT_ERREICHBAR = 'In Ihrer Rolle nicht erreichbar';

interface ZahlEintrag {
  label: string;
  wert: number | string | null;
  einheit: string | null;
  ziel: BoardZiel | null;
}

interface Props {
  kachel: BoardKachelDaten;
  /** Nach einer Aktion auf einer Belegkarte: Board neu laden. */
  onChange?: () => void;
  /** Verwaltungs-Tabs, die der Benutzer sieht (null: Rollen noch nicht geladen). Ziele
      außerhalb werden nicht klickbar dargestellt, statt ins Leere zu führen. */
  erreichbareTabs: string[] | null;
}

function zahlText(z: ZahlEintrag): string {
  if (z.wert === null || z.wert === undefined) return '–';
  if (typeof z.wert !== 'number') return z.einheit ? `${z.wert} ${z.einheit}` : String(z.wert);
  const geld = z.einheit === '€';
  const zahl = z.wert.toLocaleString('de-DE', geld
    ? { minimumFractionDigits: 2, maximumFractionDigits: 2 }
    : { maximumFractionDigits: 1 });
  return z.einheit ? `${zahl} ${z.einheit}` : zahl;
}

/** "2026-10-20" bzw. ISO-Zeitstempel -> "20.10.2026"; vor heute = überfällig. */
function faelligInfo(iso: string): { text: string; ueberfaellig: boolean } {
  const [j, m, t] = iso.slice(0, 10).split('-').map(Number);
  if (!j || !m || !t) return { text: iso, ueberfaellig: false };
  const tag = new Date(j, m - 1, t);
  const jetzt = new Date();
  const heute = new Date(jetzt.getFullYear(), jetzt.getMonth(), jetzt.getDate());
  return {
    text: tag.toLocaleDateString('de-DE', { day: '2-digit', month: '2-digit', year: 'numeric' }),
    ueberfaellig: tag < heute,
  };
}

interface InhaltProps {
  ziel: BoardZiel | null;
  erreichbareTabs: string[] | null;
}

const Liste: React.FC<InhaltProps & { eintraege: BoardEintrag[] }> = ({ eintraege, ziel, erreichbareTabs }) => (
  <ul className="k-kachel__liste">
    {eintraege.map((e, i) => {
      const zeilenZiel = e.ziel ?? ziel;
      const erreichbar = !!zeilenZiel && zielErreichbar(zeilenZiel, erreichbareTabs);
      const faellig = e.faellig_am ? faelligInfo(e.faellig_am) : null;
      const inhalt = (
        <>
          <span className="k-kachel__zeile-text">
            <span className="k-kachel__text">{e.text}</span>
            {e.untertext && <span className="k-kachel__untertext">{e.untertext}</span>}
          </span>
          <span className="k-kachel__zeile-meta">
            {faellig && (
              <span className={'k-kachel__faellig' + (faellig.ueberfaellig ? ' is-ueberfaellig' : '')}>
                {faellig.text}
              </span>
            )}
            {e.status && <span className="k-kachel__badge">{e.status}</span>}
          </span>
        </>
      );
      return (
        <li key={zeilenSchluessel(i, e.id)}>
          {zeilenZiel && erreichbar ? (
            <button type="button" className="k-kachel__zeile" onClick={() => navigiere(zeilenZiel)}>
              {inhalt}
            </button>
          ) : (
            <div className="k-kachel__zeile" title={zeilenZiel ? NICHT_ERREICHBAR : undefined}>{inhalt}</div>
          )}
        </li>
      );
    })}
  </ul>
);

const Zahlen: React.FC<InhaltProps & { eintraege: ZahlEintrag[] }> = ({ eintraege, ziel, erreichbareTabs }) => (
  <div className="k-kachel__zahlen">
    {eintraege.map(z => {
      const zahlZiel = z.ziel ?? ziel;
      const erreichbar = !!zahlZiel && zielErreichbar(zahlZiel, erreichbareTabs);
      const inhalt = (
        <>
          <span className="k-kachel__zahl-label">{z.label}</span>
          <span className="k-kachel__zahl-wert">{zahlText(z)}</span>
        </>
      );
      return zahlZiel && erreichbar ? (
        <button key={z.label} type="button" className="k-kachel__zahl" onClick={() => navigiere(zahlZiel)}>
          {inhalt}
        </button>
      ) : (
        <div key={z.label} className="k-kachel__zahl" title={zahlZiel ? NICHT_ERREICHBAR : undefined}>
          {inhalt}
        </div>
      );
    })}
  </div>
);

/** Eine Kachel des Rollen-Dashboards: Kopf mit Zähler, Inhalt je `art`,
 *  „Alle anzeigen“, wenn das Backend mehr Einträge zählt, als es mitschickt. */
const BoardKachel: React.FC<Props> = ({ kachel, onChange, erreichbareTabs }) => {
  const ziel = kachel.ziel;
  // Nur bei Belegen/Listen zählt `anzahl` Einträge; bei Kennzahlen und
  // Wochenplan zählt es etwas anderes (fehlende Tage, Einsätze).
  const mehr = !kachel.fehler && ziel !== null
    && (kachel.art === 'belege' || kachel.art === 'liste')
    && kachel.anzahl > kachel.eintraege.length;

  let inhalt: React.ReactNode = null;
  if (kachel.fehler) {
    inhalt = <p className="k-kachel__fehler">Kachel konnte nicht geladen werden</p>;
  } else if (kachel.art === 'belege') {
    inhalt = (
      <div className="k-kachel__belege">
        {(kachel.eintraege as BelegKarteDaten[]).map(k => (
          <BelegKarte key={`${k.typ}-${k.id}`} karte={k} onChange={() => onChange?.()} />
        ))}
      </div>
    );
  } else if (kachel.art === 'liste') {
    inhalt = <Liste eintraege={kachel.eintraege as BoardEintrag[]} ziel={ziel} erreichbareTabs={erreichbareTabs} />;
  } else if (kachel.art === 'zahlen') {
    inhalt = <Zahlen eintraege={kachel.eintraege as ZahlEintrag[]} ziel={ziel} erreichbareTabs={erreichbareTabs} />;
  } else if (kachel.art === 'wochenplan') {
    inhalt = <Wochenplan tage={kachel.eintraege as WochenTag[]} ziel={ziel} erreichbareTabs={erreichbareTabs} />;
  }

  return (
    <article className={`k-kachel k-kachel--${kachel.art}` + (kachel.fehler ? ' is-fehler' : '')}>
      <header className="k-kachel__kopf">
        <h3 className="k-kachel__titel">{kachel.titel}</h3>
        {!kachel.fehler && <span className="k-kachel__zaehler">{kachel.anzahl}</span>}
      </header>
      <div className="k-kachel__inhalt">{inhalt}</div>
      {(mehr || kachel.quelle) && (
        <footer className="k-kachel__fuss">
          {kachel.quelle && <span className="k-kachel__quelle">{kachel.quelle}</span>}
          {mehr && ziel && (zielErreichbar(ziel, erreichbareTabs) ? (
            <button
              type="button"
              className="k-btn k-btn--ghost k-btn--sm k-kachel__alle"
              onClick={() => navigiere(ziel)}
            >
              Alle anzeigen ({kachel.anzahl})
            </button>
          ) : (
            <span className="k-kachel__alle is-gesperrt" title={NICHT_ERREICHBAR}>
              {kachel.anzahl} insgesamt
            </span>
          ))}
        </footer>
      )}
    </article>
  );
};

export default BoardKachel;
